import React, { useState } from 'react';
import { Eye, Edit3, MoreHorizontal, ArrowRight, Mail, Building2 } from 'lucide-react';
import ClientStatusBadge from './ClientStatusBadge';
import ClientResponsible from './ClientResponsible';
import ClientFinancialStatus from './ClientFinancialStatus';
import ClientLastContact from './ClientLastContact';

interface ClientRowProps {
  company: any;
  onSelect: (company: any) => void;
  onEdit: (company: any, e: React.MouseEvent) => void;
  onViewModal?: (company: any, e?: React.MouseEvent) => void;
  onNavigate?: (company: any, e?: React.MouseEvent) => void;
  selectedCompanyIds: string[];
  toggleSelection: (id: string, e: React.MouseEvent) => void;
}

export default function ClientRow({
  company, onSelect, onEdit, onViewModal, onNavigate, selectedCompanyIds, toggleSelection
}: ClientRowProps) {
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  const getInitials = (name: string) => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length > 0 && parts[0] !== '') {
      if (parts.length > 1) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }
    return '?';
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Navigate or select company on row click
    if (onNavigate) {
      onNavigate(company, e);
    } else {
      onSelect(company);
    }
  };

  const isChecked = selectedCompanyIds.includes(company.id.toString());

  return (
    <tr 
      onClick={handleRowClick}
      className="hover:bg-slate-50/75 transition-all duration-150 cursor-pointer group/row"
    >
      {/* Checkbox */}
      <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
        <input 
          type="checkbox" 
          checked={isChecked}
          onChange={(e) => toggleSelection(company.id.toString(), e as any)}
          className="w-4.5 h-4.5 rounded-lg border-slate-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer transition-all hover:border-slate-400" 
        />
      </td>

      {/* Cliente Column */}
      <td className="py-4 px-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl border border-[#0F172A0D] flex items-center justify-center shadow-3xs shrink-0 overflow-hidden bg-white relative">
            {company.logoUrl ? (
              <img 
                src={company.logoUrl} 
                alt={company.name} 
                referrerPolicy="no-referrer"
                className="w-full h-full object-contain p-1.5" 
              />
            ) : (
              <div className="w-full h-full bg-[#0F172A] text-white flex items-center justify-center font-bold text-xs">
                {getInitials(company.name)}
              </div>
            )}
          </div>
          <div className="flex flex-col text-left">
            <h4 className="font-bold text-slate-800 text-sm group-hover/row:text-indigo-600 transition-colors leading-snug">
              {company.name}
            </h4>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[11px] font-semibold text-slate-400">{company.industry || 'Tecnologia'}</span>
              <span className="w-1 h-1 rounded-full bg-slate-300" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Mail size={10} className="text-slate-300" />
                {company.email || `contato@${company.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com.br`}
              </span>
            </div>
          </div>
        </div>
      </td>

      {/* Responsável Column */}
      <td className="py-4 px-3">
        <ClientResponsible 
          name={company.responsibleName || 'Equipe Comercial'} 
          role={company.responsibleRole}
        />
      </td>

      {/* Projetos Column */}
      <td className="py-4 px-3">
        <div className="flex flex-col items-start justify-center">
          <span className={`px-2.5 py-1.5 rounded-xl text-xs font-bold leading-none ${
            company.projects > 0 
              ? 'bg-slate-100 text-slate-700 border border-slate-200/50' 
              : 'bg-slate-50 text-slate-400 border border-slate-100'
          }`}>
            {company.projects > 0 ? `${company.projects} ativos` : 'Nenhum'}
          </span>
        </div>
      </td>

      {/* Último Contato Column */}
      <td className="py-4 px-3">
        <ClientLastContact days={company.lastContactDays !== undefined ? company.lastContactDays : 3} />
      </td>

      {/* Financeiro Column */}
      <td className="py-4 px-3">
        <ClientFinancialStatus status={company.financialStatus || 'Em dia'} />
      </td>

      {/* Status Column */}
      <td className="py-4 px-3">
        <ClientStatusBadge status={company.status || 'Ativo'} />
      </td>

      {/* Ações Column */}
      <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        {/* Hover quick action buttons for desktop, fallback list for safety */}
        <div className="flex items-center justify-end gap-1.5 opacity-0 group-hover/row:opacity-100 transition-opacity duration-150">
          <button 
            type="button"
            onClick={(e) => { e.stopPropagation(); onViewModal?.(company, e); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition-colors cursor-pointer border border-transparent hover:border-indigo-100/30"
            title="Visualizar Detalhes (Ficha)"
          >
            <Eye size={15} />
          </button>
          <button 
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(company, e); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition-colors cursor-pointer border border-transparent hover:border-indigo-100/30"
            title="Editar Cadastro"
          >
            <Edit3 size={15} />
          </button>
          
          <div className="w-[1px] h-3.5 bg-slate-200/80 mx-0.5" />

          <button 
            type="button"
            onClick={(e) => { e.stopPropagation(); onNavigate?.(company, e); }}
            className="px-3 py-1.5 rounded-xl bg-[#0F172A] text-white hover:bg-slate-800 transition-all cursor-pointer flex items-center gap-1.5 text-[11px] font-bold shadow-3xs hover:scale-[1.02]"
            title="Acessar Visão 360°"
          >
            <span>Gerenciar</span>
            <ArrowRight size={13} />
          </button>
        </div>

        {/* Regular list for non-hover, subtle menu */}
        <div className="group-hover/row:hidden text-slate-400">
          <MoreHorizontal size={16} />
        </div>
      </td>
    </tr>
  );
}
